import React from "react";
import { Alert, Select, Space, Table, Tag, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import {
  AmountFormat,
  BankFormat,
  ColumnMapping,
  DateFormat,
  TransactionField,
  detectAmountFormat,
  detectDateFormat,
} from "../bankFormats";
import { TRANSACTION_FIELDS } from "../parseCsv";

const { Text } = Typography;

const REQUIRED_FIELDS: TransactionField[] = ["amount", "paymentDate"];

const AMOUNT_FORMAT_OPTIONS: AmountFormat[] = ["german", "international"];
const DATE_FORMAT_OPTIONS: DateFormat[] = [
  "iso",
  "de-dot",
  "de-slash",
  "de-dash",
];

/** Maps "de-dot" → "de_dot" so it can index a flat i18n key namespace. */
const dateFormatI18nKey = (df: DateFormat) => df.replace(/-/g, "_");

interface Props {
  mappings: ColumnMapping[];
  detectedFormat: BankFormat | null;
  /** First few rows of the CSV, used for sample display and amount-format detection */
  previewRows: Record<string, string>[];
  onChange: (mappings: ColumnMapping[]) => void;
}

const MappingStep = ({
  mappings,
  detectedFormat,
  previewRows,
  onChange,
}: Props) => {
  const { t } = useTranslation("CsvImport");

  const fieldLabel = (field: TransactionField): string =>
    t(`field${field.charAt(0).toUpperCase()}${field.slice(1)}` as any);

  /**
   * For a given CSV column, build the Select options.
   * Fields already assigned to another column are disabled and show where they're used.
   * "ignore" is always selectable (multiple columns can be ignored).
   */
  const getOptionsForColumn = (currentColumn: string) => {
    // Map: targetField → the csvColumn it's already assigned to (excluding current)
    const assignedTo: Partial<Record<TransactionField, string>> = {};
    mappings.forEach((m) => {
      if (m.csvColumn !== currentColumn && m.targetField !== "ignore") {
        assignedTo[m.targetField] = m.csvColumn;
      }
    });

    return TRANSACTION_FIELDS.map((field) => {
      const takenBy = assignedTo[field];
      return {
        value: field,
        label: takenBy
          ? `${fieldLabel(field)} (→ ${takenBy})`
          : fieldLabel(field),
        disabled: !!takenBy,
      };
    });
  };

  const handleChange = (csvColumn: string, targetField: TransactionField) => {
    onChange(
      mappings.map((m) => {
        if (m.csvColumn !== csvColumn) return m;
        const next: ColumnMapping = { ...m, targetField };
        // Auto-detect amountFormat the first time this column becomes the amount column
        if (
          targetField === "amount" &&
          m.targetField !== "amount" &&
          !next.amountFormat
        ) {
          const samples = previewRows.map((r) => r[csvColumn] ?? "");
          next.amountFormat =
            detectedFormat?.defaultAmountFormat ?? detectAmountFormat(samples);
        }
        // Same for dateFormat when the column becomes the paymentDate column
        if (
          targetField === "paymentDate" &&
          m.targetField !== "paymentDate" &&
          !next.dateFormat
        ) {
          const samples = previewRows.map((r) => r[csvColumn] ?? "");
          next.dateFormat =
            detectedFormat?.defaultDateFormat ?? detectDateFormat(samples);
        }
        return next;
      })
    );
  };

  const handleAmountFormatChange = (
    csvColumn: string,
    amountFormat: AmountFormat
  ) => {
    onChange(
      mappings.map((m) =>
        m.csvColumn === csvColumn ? { ...m, amountFormat } : m
      )
    );
  };

  const handleDateFormatChange = (
    csvColumn: string,
    dateFormat: DateFormat
  ) => {
    onChange(
      mappings.map((m) =>
        m.csvColumn === csvColumn ? { ...m, dateFormat } : m
      )
    );
  };

  const usedFields = mappings
    .filter((m) => m.targetField !== "ignore")
    .map((m) => m.targetField);

  const missingRequired = REQUIRED_FIELDS.filter(
    (f) => !usedFields.includes(f)
  );

  const columns = [
    {
      title: t("mappingColCsv"),
      dataIndex: "csvColumn",
      key: "csvColumn",
      render: (col: string) => <Text strong>{col}</Text>,
    },
    {
      title: t("mappingColSample"),
      dataIndex: "csvColumn",
      key: "sample",
      render: (col: string) => (
        <Text type="secondary">{previewRows[0]?.[col] ?? "—"}</Text>
      ),
    },
    {
      title: t("mappingColMapsTo"),
      dataIndex: "targetField",
      key: "targetField",
      render: (field: TransactionField, record: ColumnMapping) => (
        <Select
          size="small"
          value={field}
          style={{ width: 240 }}
          options={getOptionsForColumn(record.csvColumn)}
          onChange={(val) => handleChange(record.csvColumn, val)}
        />
      ),
    },
    {
      title: t("mappingColFormat"),
      key: "format",
      render: (_: unknown, record: ColumnMapping) => {
        if (record.targetField === "amount") {
          return (
            <Select<AmountFormat>
              size="small"
              value={record.amountFormat ?? "international"}
              style={{ width: 200 }}
              options={AMOUNT_FORMAT_OPTIONS.map((af) => ({
                value: af,
                label: t(`amountFormat_${af}` as const),
              }))}
              onChange={(val) =>
                handleAmountFormatChange(record.csvColumn, val)
              }
            />
          );
        }
        if (record.targetField === "paymentDate") {
          return (
            <Select<DateFormat>
              size="small"
              value={record.dateFormat ?? "iso"}
              style={{ width: 200 }}
              options={DATE_FORMAT_OPTIONS.map((df) => ({
                value: df,
                label: t(`dateFormat_${dateFormatI18nKey(df)}` as const),
              }))}
              onChange={(val) => handleDateFormatChange(record.csvColumn, val)}
            />
          );
        }
        return <Text type="secondary">—</Text>;
      },
    },
  ];

  return (
    <div>
      {detectedFormat ? (
        <Alert
          type="success"
          message={t("mappingFormatDetected", { format: detectedFormat.label })}
          style={{ marginBottom: 16 }}
          showIcon
        />
      ) : (
        <Alert
          type="warning"
          message={t("mappingFormatUnknown")}
          description={t("mappingFormatUnknownDescription")}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {missingRequired.length > 0 && (
        <Alert
          type="warning"
          message={
            <>
              {t("mappingMissingFields")}{" "}
              {missingRequired.map((f) => (
                <Tag key={f} color="orange">
                  {fieldLabel(f)}
                </Tag>
              ))}
            </>
          }
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      <Table
        dataSource={mappings}
        columns={columns}
        rowKey="csvColumn"
        pagination={false}
        size="small"
      />

      <Space style={{ marginTop: 12 }}>
        <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t("mappingCurrencyNote")}
        </Typography.Text>
      </Space>
    </div>
  );
};

export default MappingStep;
